class YoutubeEmbed extends HTMLElement {
  constructor() {
    super();
    this._handleEscape = this._handleEscape.bind(this);
    this._handleClose = this._handleClose.bind(this);
    this._handleOpen = this._handleOpen.bind(this);
    this._handleModalClick = this._handleModalClick.bind(this);
  }

  connectedCallback() {
    // Defer to next microtask so child elements are parsed before we query them.
    // Custom element connectedCallback fires during parsing, before children exist.
    Promise.resolve().then(() => {
      if (!this.isConnected) return;
      this._init();
    });
  }

  _init() {
    this._trigger = this.querySelector('.featured-youtube__trigger');
    this._modal = this.querySelector('.featured-youtube__modal');
    this._player = this.querySelector('.featured-youtube__player');
    this._closeBtn = this.querySelector('.featured-youtube__close');
    this._video = this.querySelector('.featured-youtube__video');
    this._sourceType = this.dataset.sourceType || 'youtube';
    this._videoId = this.dataset.videoId || '';
    this._host = this.dataset.host || 'https://www.youtube-nocookie.com/embed/';

    if (this._trigger) {
      this._trigger.addEventListener('click', this._handleOpen);
    }
    if (this._modal) {
      this._modal.addEventListener('click', this._handleModalClick);
    }
    if (this._closeBtn) {
      this._closeBtn.addEventListener('click', this._handleClose);
    }
    document.addEventListener('keydown', this._handleEscape);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._handleEscape);
    if (this._trigger) this._trigger.removeEventListener('click', this._handleOpen);
    if (this._modal) this._modal.removeEventListener('click', this._handleModalClick);
    if (this._closeBtn) this._closeBtn.removeEventListener('click', this._handleClose);
  }

  _handleOpen() {
    if (!this._modal || !this._player) return;

    if (this._sourceType === 'uploaded') {
      if (!this._video) return;
    } else {
      if (!this._videoId) return;
      if (!this._player.querySelector('iframe')) {
        const iframe = document.createElement('iframe');
        iframe.src = this._host + this._videoId + '?autoplay=1&rel=0';
        iframe.title = 'YouTube video player';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('frameborder', '0');
        this._player.appendChild(iframe);
      }
    }

    this._modal.hidden = false;
    document.body.style.overflow = 'hidden';

    if (this._sourceType === 'uploaded' && this._video) {
      const playPromise = this._video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* autoplay blocked — user can press play */ });
      }
    }

    if (this._closeBtn && typeof this._closeBtn.focus === 'function') {
      this._closeBtn.focus();
    }
  }

  _handleClose() {
    if (!this._modal) return;
    this._modal.hidden = true;
    document.body.style.overflow = '';

    if (this._sourceType === 'uploaded') {
      if (this._video) {
        this._video.pause();
        try { this._video.currentTime = 0; } catch (e) { /* ignore */ }
      }
    } else {
      const iframe = this._player && this._player.querySelector('iframe');
      if (iframe) iframe.remove();
    }

    if (this._trigger && typeof this._trigger.focus === 'function') {
      this._trigger.focus();
    }
  }

  _handleEscape(event) {
    if (event.key === 'Escape' && this._modal && !this._modal.hidden) {
      this._handleClose();
    }
  }

  _handleModalClick(event) {
    if (!event.target) return;
    const insidePlayer = event.target.closest('.featured-youtube__player');
    const onCloseBtn = event.target.closest('.featured-youtube__close');
    if (!insidePlayer && !onCloseBtn) {
      this._handleClose();
    }
  }
}

if (!customElements.get('youtube-embed')) {
  customElements.define('youtube-embed', YoutubeEmbed);
}
